import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { cn } from '@/lib/utils';
import { GUIDE_TRACKS } from './tracks';

/**
 * Guide hub — the tenant-facing learning centre. Each card is a "track" that
 * teaches one part of the platform end to end. Procurement is live; others are
 * staged as coming-soon so the structure is visible while content is written.
 */
export function GuideHubPage() {
  const live = GUIDE_TRACKS.filter((t) => t.status === 'live');
  const soon = GUIDE_TRACKS.filter((t) => t.status === 'soon');

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Guide"
        title="Learn Zafroi, end to end"
        description="Step-by-step walkthroughs of how to run your business on the platform — with flows, diagrams and worked examples. Start with a track below."
      />

      {/* Featured / live tracks */}
      <div className="grid gap-4 md:grid-cols-2">
        {live.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.id}
              to={`/guide/${t.id}`}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className={cn('absolute inset-x-0 top-0 h-24 bg-gradient-to-b opacity-70', t.accent)} aria-hidden />
              <div className="relative space-y-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-card text-primary ring-1 ring-inset ring-primary/20 shadow-sm">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-primary">{t.tagline}</div>
                    <h2 className="text-[17px] font-semibold tracking-tight text-foreground">{t.title}</h2>
                  </div>
                </div>
                <p className="text-[13px] leading-relaxed text-muted-foreground">{t.description}</p>
                <div className="flex items-center justify-between pt-1">
                  <span className="inline-flex items-center gap-3 text-[11.5px] text-muted-foreground">
                    {t.minutes && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" /> {t.minutes} min
                      </span>
                    )}
                    {t.chapters && <span>{t.chapters.length} chapters</span>}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-primary">
                    Start <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Coming soon */}
      {soon.length > 0 && (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> More tracks coming soon
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {soon.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.id} className="rounded-2xl border border-dashed border-border bg-muted/20 p-5">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-card text-muted-foreground ring-1 ring-inset ring-border/70">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[14px] font-semibold leading-tight text-foreground">{t.title}</h3>
                    <div className="text-[11px] text-muted-foreground">{t.tagline}</div>
                  </div>
                </div>
                <p className="mt-3 text-[12.5px] leading-relaxed text-muted-foreground">{t.description}</p>
                {t.topics && (
                  <ul className="mt-3 space-y-1">
                    {t.topics.map((topic) => (
                      <li key={topic} className="flex items-center gap-2 text-[12px] text-muted-foreground">
                        <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                  Coming soon
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
